import {API_CONFIG} from '../../contentGenerator/config';
import {apiFetch} from '../apiFetch';

const LEGACY_STORAGE_KEY = 'cg-brand-profile';

// ── Brand profile shape ─────────────────────────────────────────────

export const DEFAULT_SHOPIFY_MAPPING = {
    title: {source: 'product.title', type: 'native'},
    description: {source: 'product.body_html', type: 'native'},
    bullets: {source: 'product.body_html', type: 'html_list'},
    // type: 'native' = built-in Shopify field
    // type: 'html_list' = extract/inject <li> items from body_html
    // type: 'metafield' = read/write a specific metafield (source = 'namespace.key')
};

export const EMPTY_BRAND_PROFILE = {
    companyName: '',
    websiteUrl: '',
    tagline: '',
    personality: [],
    targetAudience: '',
    voiceStyle: '',
    toneTags: [],
    personPov: '2nd',
    wordsLove: [],
    wordsAvoid: [],
    primaryKeywords: [],
    secondaryKeywords: [],
    productKnowledge: '',
    contentRules: '',
    shopifyMapping: {...DEFAULT_SHOPIFY_MAPPING},
    rawAnalysis: '',
    createdAt: null,
    updatedAt: null,
};

// ── CRUD ─────────────────────────────────────────────────────────────

function readLegacyBrandProfile() {
    try {
        const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function clearLegacyBrandProfile() {
    try {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
        // Ignore localStorage cleanup failures.
    }
}

function normalizeSavedProfile(profile) {
    const payload = {...profile, updatedAt: new Date().toISOString()};
    if (!payload.createdAt) payload.createdAt = payload.updatedAt;
    return payload;
}

async function parseApiResponse(response, fallbackMessage) {
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) {
        throw new Error(
            payload?.error ||
                payload?.message ||
                (typeof payload === 'string' && payload) ||
                fallbackMessage,
        );
    }

    return payload;
}

export async function loadBrandProfile() {
    try {
        const response = await apiFetch(`${API_CONFIG.PROXY_URL}/brand-profile`);
        const profile = await parseApiResponse(response, 'Could not load the brand profile.');

        if (profile?.companyName) {
            clearLegacyBrandProfile();
            return profile;
        }
    } catch {
        // Fall back to the legacy browser-stored profile below.
    }

    const legacyProfile = readLegacyBrandProfile();
    if (!legacyProfile?.companyName) return null;

    try {
        return await saveBrandProfile(legacyProfile);
    } catch {
        return legacyProfile;
    }
}

export async function saveBrandProfile(profile) {
    const payload = normalizeSavedProfile(profile);
    const response = await apiFetch(`${API_CONFIG.PROXY_URL}/brand-profile`, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
    });

    await parseApiResponse(response, 'Could not save the brand profile.');
    clearLegacyBrandProfile();

    return payload;
}

// ── Metafield definition creation ────────────────────────────────────

const METAFIELD_TYPE_MAP = {
    title: {type: 'single_line_text_field', name: 'SEO Title', description: 'Product title for content generation'},
    description: {type: 'multi_line_text_field', name: 'SEO Description', description: 'Product description for content generation'},
    bullets: {type: 'list.single_line_text_field', name: 'Bullet Points', description: 'Product bullet points list'},
};

export async function ensureMetafieldDefinitions(shopifyMapping) {
    if (!shopifyMapping) return;

    const definitions = [];
    for (const [fieldKey, mapping] of Object.entries(shopifyMapping)) {
        if (mapping.type !== 'metafield' || !mapping.source) continue;
        const [namespace, key] = mapping.source.split('.');
        if (!namespace || !key) continue;

        const meta = METAFIELD_TYPE_MAP[fieldKey] || {type: 'single_line_text_field', name: mapping.source, description: ''};
        // Metafield mappings are variant-level — each variant gets its own content
        definitions.push({
            namespace,
            key,
            name: meta.name,
            type: meta.type,
            description: meta.description,
            ownerType: 'PRODUCTVARIANT',
        });
    }

    if (!definitions.length) return;

    try {
        const res = await apiFetch(`${API_CONFIG.PROXY_URL}/metafield-definitions`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({definitions}),
        });
        if (res.ok) {
            const data = await res.json();
            console.log('Metafield definitions:', data.results);
            return data.results;
        }
    } catch (err) {
        console.warn('Failed to create metafield definitions:', err.message);
    }
}

// ── AI-powered brand analysis (GPT with web browsing) ───────────────

export async function analyzeBrand(websiteUrl, companyName) {
    const response = await apiFetch(`${API_CONFIG.PROXY_URL}/brand/analyze`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            websiteUrl,
            companyName,
        }),
    });

    const data = await parseApiResponse(response, 'AI analysis failed.');
    const parsed = data?.profile || {};

    return {
        ...EMPTY_BRAND_PROFILE,
        ...parsed,
        websiteUrl: parsed.websiteUrl || websiteUrl,
        rawAnalysis: parsed.rawAnalysis || '',
    };
}

// ── Build prompt context from brand profile ─────────────────────────

export function buildBrandContext(profile) {
    if (!profile?.companyName) return '';

    const lines = [`Brand: ${profile.companyName}`];
    if (profile.tagline) lines.push(`Positioning: ${profile.tagline}`);
    if (profile.personality?.length) lines.push(`Brand personality: ${profile.personality.join(', ')}`);
    if (profile.targetAudience) lines.push(`Target audience: ${profile.targetAudience}`);
    if (profile.voiceStyle) lines.push(`Writing style: ${profile.voiceStyle}`);
    if (profile.toneTags?.length) lines.push(`Tone: ${profile.toneTags.join(', ')}`);
    if (profile.personPov) lines.push(`Point of view: ${profile.personPov} person`);
    if (profile.wordsLove?.length) lines.push(`Preferred vocabulary: ${profile.wordsLove.join(', ')}`);
    if (profile.wordsAvoid?.length) lines.push(`Avoid these words: ${profile.wordsAvoid.join(', ')}`);
    if (profile.primaryKeywords?.length) lines.push(`Primary keywords: ${profile.primaryKeywords.join(', ')}`);
    if (profile.productKnowledge) lines.push(`Product knowledge: ${profile.productKnowledge}`);
    if (profile.contentRules) lines.push(`Content rules: ${profile.contentRules}`);

    return `\n\nBrand guidelines:\n${lines.join('\n')}`;
}
